import { useRef } from 'react';
import type { TenantDetail } from '@sellokart/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Alert } from '@/components/Alert';
import { Button } from '@/components/Button';
import { SectionCard } from '@/components/SectionCard';
import {
  deleteTenantLogo,
  tenantKeys,
  uploadTenantLogo,
} from '@/features/tenants/api';
import { API_URL, getErrorMessage } from '@/lib/api';

const ACCEPT = 'image/png,image/jpeg,image/webp';

/**
 * Its own section rather than a field inside "Edit tenant": replace and remove
 * take effect immediately, with no ConfirmDialog. Unlike deactivating a tenant,
 * a logo change is fully reversible and locks nobody out, so it does not earn
 * an "are you sure".
 */
export function TenantLogoSection({ tenant }: { tenant: TenantDetail }) {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: tenantKeys.all });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => uploadTenantLogo(tenant.id, file),
    onSuccess: () => void invalidate(),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteTenantLogo(tenant.id),
    onSuccess: () => void invalidate(),
  });

  const pending = uploadMutation.isPending || deleteMutation.isPending;
  const error = uploadMutation.isError
    ? uploadMutation.error
    : deleteMutation.isError
      ? deleteMutation.error
      : undefined;

  return (
    <SectionCard
      headingId="tenant-logo"
      title="Logo"
      description="Shown in this shop's own portal sidebar."
    >
      <div className="flex items-center gap-5 px-5 py-4">
        <span
          aria-hidden="true"
          className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400"
        >
          {tenant.logoUrl ? (
            // logoUrl is relative to the API origin, so it is joined onto
            // API_URL here rather than stored absolute — see the DTO comment.
            <img
              src={`${API_URL}${tenant.logoUrl}`}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            'No logo'
          )}
        </span>
        <div className="flex flex-col gap-2">
          {error ? <Alert tone="error">{getErrorMessage(error)}</Alert> : null}
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={pending}
            >
              {tenant.logoUrl ? 'Replace' : 'Upload'}
            </Button>
            {tenant.logoUrl ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                onClick={() => deleteMutation.mutate()}
                disabled={pending}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <p className="hint">PNG, JPEG or WebP, up to 2MB.</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          disabled={pending}
          onChange={(event) => {
            // No client-side size or type pre-check: the server validates both
            // and its message surfaces through the same error path above. An
            // infrequent, admin-only upload does not need that logic duplicated
            // here just to save one round trip.
            const file = event.target.files?.[0];
            if (file) uploadMutation.mutate(file);
            // Cleared so choosing the SAME file again still fires a change.
            event.target.value = '';
          }}
        />
      </div>
    </SectionCard>
  );
}
